import { getDetector } from '../detectionProvider'
import FrameSampler from '../frameSampler'
import ObjectTracker from '../objectTracker'
import { makePathProgress, stepPathProgress } from '../pathUtils'

// Shared config so the play screen can drive the path + target and read the
// crossing log without deep prop plumbing.
export const trackingState = {
    // Multi-point crossing path in normalized (0-1) video space (bezier drawn).
    // Empty by default — the user draws it on the play screen.
    pathPoints: [],
    pathDone: false, // true once the user double-clicks to finish drawing
    target: 'all', // class name to track/count, or 'all'
    lineCrossing: false, // draw + count crossings only when enabled
    trajectory: false, // draw the persistent path of the primary target
    trajectoryId: null, // locked tracked id for the trajectory (null = largest)
    crossings: [], // recent { id, class, dir, at }
    counts: {}, // total crossings per class
    classesSeen: [], // classes currently/ recently detected (for the dropdown)
    trackedIds: [], // live { id, class } for the trajectory id picker
    _traj: [], // internal: primary-target centroid path (normalized)
}

export function resetTrackingState() {
    trackingState.crossings = []
    trackingState.counts = {}
    trackingState.classesSeen = []
    trackingState.trackedIds = []
    trackingState._traj = []
}

// Multi-object tracking + per-id line crossing. coco-ssd supplies detections;
// ObjectTracker assigns persistent ids (short-term re-id by appearance).
export default function createTrackingDetector() {
    let model = null
    let sampler = null
    let tracker = null
    const progress = {} // id -> ordered path-progress state (waypoint walk)

    return {
        id: 'tracking',
        label: 'Object Tracking',
        accent: '#22d3ee',

        async load() {
            model = await getDetector()
            if (!sampler) sampler = new FrameSampler()
            if (!tracker)
                tracker = new ObjectTracker({ maxAge: 30, minHits: 2 })
        },

        reset() {
            if (tracker) tracker.reset()
            Object.keys(progress).forEach((k) => delete progress[k])
            resetTrackingState()
        },

        async detect({ video, vw, vh, stamp }) {
            if (!model || !vw || !vh) return null
            let results = await model.detect(video, 20)
            results = results.filter((r) => r.score > 0.45)

            // Track only the chosen class (or everything).
            const target = trackingState.target
            const dets = results
                .filter((r) => target === 'all' || r.class === target)
                .map((r) => ({
                    class: r.class,
                    score: r.score,
                    x: r.bbox[0] / vw,
                    y: r.bbox[1] / vh,
                    w: r.bbox[2] / vw,
                    h: r.bbox[3] / vh,
                }))

            // Class list for the dropdown (union of what's on screen).
            const seen = new Set(trackingState.classesSeen)
            results.forEach((r) => seen.add(r.class))
            trackingState.classesSeen = Array.from(seen).sort()

            const imageData = sampler.sample(video, stamp)
            const tracks = tracker.update(dets, imageData)

            // Live id list for the trajectory picker.
            trackingState.trackedIds = tracks.map((t) => ({
                id: t.id,
                class: t.class,
            }))

            // Persistent trajectory of the chosen tracked id, else the largest.
            if (trackingState.trajectory && tracks.length) {
                let primary = null
                if (trackingState.trajectoryId != null) {
                    primary =
                        tracks.find(
                            (t) =>
                                String(t.id) ===
                                String(trackingState.trajectoryId)
                        ) || null
                } else {
                    primary = tracks.reduce(
                        (best, t) =>
                            !best || t.w * t.h > best.w * best.h ? t : best,
                        null
                    )
                }
                if (primary) {
                    trackingState._traj.push({ x: primary.cx, y: primary.cy })
                    if (trackingState._traj.length > 1500)
                        trackingState._traj.shift()
                }
            } else if (!trackingState.trajectory) {
                trackingState._traj = []
            }

            // Per-id path following (only when the toggle is on): the object
            // must travel through EVERY point of the path in order; completing
            // it counts as one "passed".
            const pathObj = { points: trackingState.pathPoints || [] }
            const now = Date.now()
            const live = new Set()
            tracks.forEach((t) => {
                live.add(t.id)
                const cur = { x: t.cx, y: t.cy }
                if (trackingState.lineCrossing) {
                    if (!progress[t.id]) progress[t.id] = makePathProgress()
                    const res = stepPathProgress(
                        pathObj,
                        progress[t.id],
                        { box: { x: t.x, y: t.y, w: t.w, h: t.h }, x: cur.x, y: cur.y },
                        now
                    )
                    if (res) {
                        trackingState.counts[t.class] =
                            (trackingState.counts[t.class] || 0) + 1
                        trackingState.crossings.unshift({
                            id: t.id,
                            class: t.class,
                            dir: res,
                            at: now,
                        })
                        if (trackingState.crossings.length > 8)
                            trackingState.crossings.pop()
                    }
                }
            })
            Object.keys(progress).forEach((id) => {
                if (!live.has(Number(id))) delete progress[id]
            })

            const boxes = tracks.map((t) => ({
                x: t.x,
                y: t.y,
                w: t.w,
                h: t.h,
                label: `#${t.id} ${t.class}`,
                score: t.score,
            }))
            // Persistent trajectory as a smooth bezier curve (overlayPainter).
            if (trackingState.trajectory && trackingState._traj.length > 1) {
                boxes.push({
                    path: trackingState._traj.slice(),
                    accent: '#9b6ef3',
                    curve: true,
                })
            }
            // The crossing path itself is drawn by TrackingControls (with the
            // arrows + ghost preview), so it is not pushed onto the overlay here.

            const totalCross = Object.values(trackingState.counts).reduce(
                (s, n) => s + n,
                0
            )
            const insights = [
                { label: 'Tracking', value: target },
                { label: 'Objects', value: tracks.length },
            ]
            if (trackingState.lineCrossing) {
                insights.push({ label: 'Passed', value: totalCross })
                trackingState.crossings.slice(0, 4).forEach((c) =>
                    insights.push({
                        label: `#${c.id} ${c.class}`,
                        value: c.dir,
                    })
                )
            }

            return { boxes, insights, title: 'Tracking' }
        },

        dispose() {
            model = null
            sampler = null
            tracker = null
        },
    }
}
